import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import { IntlProvider } from "react-intl";
import enMessages from "@/lib/locales/en.json";
import arMessages from "@/lib/locales/ar.json";
import { useLocaleStore, type Locale } from "@/lib/stores/localeStore";

const messages = {
  en: enMessages,
  ar: arMessages,
};

export type { Locale };

interface IntlContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const IntlContext = createContext<IntlContextType | undefined>(undefined);

export const useLocale = () => {
  const context = useContext(IntlContext);
  if (!context) {
    throw new Error("useLocale must be used within IntlProviderWrapper");
  }
  return context;
};

interface IntlProviderWrapperProps {
  children: ReactNode;
}

export const IntlProviderWrapper = ({ children }: IntlProviderWrapperProps) => {
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useLocaleStore((state) => state.setLocale);

  // Set document direction and lang attribute when locale changes
  useEffect(() => {
    const direction = locale === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", direction);
    document.documentElement.setAttribute("lang", locale);

    // Also set on body for better coverage
    document.body.setAttribute("dir", direction);

    // Update CSS custom properties for font families based on locale
    const root = document.documentElement;
    if (locale === "ar") {
      root.style.setProperty("--font-heading", "'Noto Sans Arabic', 'Tajawal', system-ui, sans-serif");
      root.style.setProperty("--font-body", "'Noto Sans Arabic', 'Tajawal', system-ui, sans-serif");
    } else {
      root.style.setProperty("--font-heading", "'MADE Okine Sans', sans-serif");
      root.style.setProperty("--font-body", "'MADE Okine Sans', sans-serif");
    }
  }, [locale]);

  return (
    <IntlContext.Provider value={{ locale, setLocale }}>
      <IntlProvider
        messages={messages[locale]}
        locale={locale}
        defaultLocale="ar"
      >
        {children}
      </IntlProvider>
    </IntlContext.Provider>
  );
};
